import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Users, UserCheck, Clock, DollarSign, FileText, CheckCircle, XCircle, AlertCircle, Plus, Search } from 'lucide-react';
import Button from '../../components/ui/Button';


import { employeeService } from '../../services/employeeService';
import { leaveService } from '../../services/leaveService';
import { approvalWorkflowService } from '../../services/approvalWorkflowService';

export default function EmployeeFileMaintenanceWorkflowHub() {
  const [activeTab, setActiveTab] = useState('employees');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Employees
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  
  // Pending Approvals
  const [pendingApprovals, setPendingApprovals] = useState([]);
  
  // Leave Requests
  const [leaveRequests, setLeaveRequests] = useState([]);
  
  // Employee Loans
  const [employeeLoans, setEmployeeLoans] = useState([]);
  
  // Pay Processing
  const [payProcessing, setPayProcessing] = useState({
    createPay: [],
    managePay: [],
    approvePay: []
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredEmployees(
        employees?.filter(emp => 
          emp?.firstName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          emp?.lastName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          emp?.employeeNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          emp?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        )
      );
    } else {
      setFilteredEmployees(employees);
    }
  }, [searchTerm, employees]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [employeesData, approvalsData, leaveData] = await Promise?.all([
        employeeService?.getAll(),
        approvalWorkflowService?.getPendingApprovals(),
        leaveService?.getAllRequests()
      ]);
      
      setEmployees(employeesData || []);
      setFilteredEmployees(employeesData || []);
      setPendingApprovals(approvalsData || []);
      setLeaveRequests(leaveData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approvalId) => {
    try {
      await approvalWorkflowService?.approveStep(approvalId, 'admin@example.com', 'Approved');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to approve');
    }
  };

  const handleReject = async (approvalId) => {
    try {
      await approvalWorkflowService?.rejectStep(approvalId, 'admin@example.com', 'Rejected');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to reject');
    }
  };

  const handleLeaveApproval = async (leaveId, status) => {
    try {
      await leaveService?.updateRequest(leaveId, {
        leaveStatus: status,
        approvedBy: 'admin@example.com',
        approvedAt: new Date()?.toISOString()
      });
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update leave request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Employee File Maintenance & Workflow Hub - PNG HR Analytics</title>
        <meta name="description" content="Comprehensive employee data management with automated approval workflows and document lifecycle tracking" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">Employee File Maintenance & Workflow Hub</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Comprehensive employee data management with automated approval workflows
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-destructive/90 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar */}
            <div className="col-span-4">
              <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-2">Overview</h2>
                  <div className="space-y-2">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Active Employees</p>
                      <p className="text-2xl font-bold text-foreground">{employees?.length}</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Pending Approvals</p>
                      <p className="text-2xl font-bold text-foreground">{pendingApprovals?.length}</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Leave Requests</p>
                      <p className="text-2xl font-bold text-foreground">{leaveRequests?.filter(l => l?.leaveStatus === 'pending')?.length}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-2">Navigation</h2>
                  <nav className="space-y-2">
                    <button
                      onClick={() => setActiveTab('employees')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        activeTab === 'employees' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                      }`}
                    >
                      <Users className="h-4 w-4 inline mr-2" />
                      Employee Records
                    </button>
                    <button
                      onClick={() => setActiveTab('approvals')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        activeTab === 'approvals' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                      }`}
                    >
                      <UserCheck className="h-4 w-4 inline mr-2" />
                      Approve Changes
                    </button>
                    <button
                      onClick={() => setActiveTab('leave')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        activeTab === 'leave' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                      }`}
                    >
                      <Clock className="h-4 w-4 inline mr-2" />
                      Leave Management
                    </button>
                    <button
                      onClick={() => setActiveTab('loans')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        activeTab === 'loans' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                      }`}
                    >
                      <DollarSign className="h-4 w-4 inline mr-2" />
                      Employee Loans
                    </button>
                    <button
                      onClick={() => setActiveTab('pay')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        activeTab === 'pay' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                      }`}
                    >
                      <FileText className="h-4 w-4 inline mr-2" />
                      Pay Processing
                    </button>
                  </nav>
                </div>
              </div>
            </div>

            {/* Main Panel */}
            <div className="col-span-8">
              {/* Employee Records */}
              {activeTab === 'employees' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground">Employee Records</h2>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Employee
                    </Button>
                  </div>
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e?.target?.value)}
                        className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {filteredEmployees?.map((employee) => (
                      <div key={employee?.id} className="p-4 border border-border rounded-lg hover:bg-muted transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground">
                              {employee?.firstName} {employee?.lastName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {employee?.employeeNumber} • {employee?.department} • {employee?.position}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              employee?.isActive
                                ? 'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
                            }`}>
                              {employee?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredEmployees?.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No employees found</p>
                    )}
                  </div>
                </div>
              )}

              {/* Approve Changes */}
              {activeTab === 'approvals' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Pending Approvals</h2>
                  <div className="space-y-3">
                    {pendingApprovals?.map((approval) => (
                      <div key={approval?.id} className="p-4 border border-border rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-foreground">{approval?.stepName}</p>
                            <p className="text-sm text-muted-foreground">
                              {approval?.entityType} • Initiated by {approval?.initiatedBy}
                            </p>
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-warning/10 text-warning">
                            Pending
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button onClick={() => handleApprove(approval?.id)} size="sm" variant="success">
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button onClick={() => handleReject(approval?.id)} size="sm" variant="destructive">
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                    {pendingApprovals?.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No pending approvals</p>
                    )}
                  </div>
                </div>
              )}

              {/* Leave Management */}
              {activeTab === 'leave' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Leave Requests</h2>
                  <div className="space-y-3">
                    {leaveRequests?.filter(l => l?.leaveStatus === 'pending')?.map((leave) => (
                      <div key={leave?.id} className="p-4 border border-border rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-foreground">
                              {leave?.employee?.firstName} {leave?.employee?.lastName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {leave?.leaveType} • {leave?.daysRequested} days
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {leave?.startDate} to {leave?.endDate}
                            </p>
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-warning/10 text-warning">
                            Pending
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button onClick={() => handleLeaveApproval(leave?.id, 'approved')} size="sm" variant="success">
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button onClick={() => handleLeaveApproval(leave?.id, 'rejected')} size="sm" variant="destructive">
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                    {leaveRequests?.filter(l => l?.leaveStatus === 'pending')?.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No pending leave requests</p>
                    )}
                  </div>
                </div>
              )}

              {/* Employee Loans */}
              {activeTab === 'loans' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground">Employee Loans</h2>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      New Loan
                    </Button>
                  </div>
                  <p className="text-center text-muted-foreground py-8">No active loans</p>
                </div>
              )}

              {/* Pay Processing */}
              {activeTab === 'pay' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Pay Processing</h2>
                  <div className="space-y-4">
                    <div className="p-4 border border-border rounded-lg">
                      <h3 className="font-medium text-foreground mb-2">Create Pay</h3>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Pay Run
                      </Button>
                    </div>
                    <div className="p-4 border border-border rounded-lg">
                      <h3 className="font-medium text-foreground mb-2">Manage Pay</h3>
                      <p className="text-sm text-muted-foreground">No pay runs in progress</p>
                    </div>
                    <div className="p-4 border border-border rounded-lg">
                      <h3 className="font-medium text-foreground mb-2">Approve Pay</h3>
                      <p className="text-sm text-muted-foreground">No pay runs pending approval</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}